import { useCallback, useState } from 'react';
import { useDispatch } from 'react-redux';

import { showAlert } from '@/slice/AlertSlice';
import { Variants } from '@/types/common';
import type { PmsSourceKey } from '@/types/pms.types';
import { useSyncProgress } from '../hooks/useSyncProgress';
import { useSetIntegrationStatusMutation } from '../services/pmsConnectionApi';
import type { PmsAccountRef } from '../services/pmsAccount';
import { describeStatusChangeError } from '../services/pmsErrorMessage';
import { syncCompletionCleared, type SyncOrigin } from '../services/pmsSyncSlice';
import { integrationStatusCopy as copy } from './status/copy';

export interface IntegrationStatusActions {
  disable: () => Promise<boolean>;
  reenable: () => Promise<boolean>;
  isDisabling: boolean;
  isReenabling: boolean;
  reenableError: string | null;
}

interface Args {
  vendorName: string;
  pmsSource: PmsSourceKey;
  /** Surface the re-enable's follow-up sync is attributed to (pmsSyncSlice.SyncOrigin). */
  origin: SyncOrigin;
  /** DV-3345: whose connection to pause/resume; null = nothing to act on. */
  account: PmsAccountRef | null;
}

/**
 * Pause / resume orchestration shared by the vendor settings page and the
 * Integrations landing hero (spec §3.6). Re-enable is two calls: PUT
 * status=ACTIVE (the backend re-validates the STORED credentials) and then a
 * body-less property sync — never a credential re-send. Progress is then
 * visible wherever useSyncProgress is rendered; nothing navigates.
 */
export function useIntegrationStatusActions({
  vendorName,
  pmsSource,
  origin,
  account,
}: Args): IntegrationStatusActions {
  const dispatch = useDispatch();
  const [setStatus] = useSetIntegrationStatusMutation();
  // skip: this instance only fires; the page's own subscriber renders progress.
  const { startSync } = useSyncProgress({ origin, account, skip: true });
  const [isDisabling, setIsDisabling] = useState(false);
  const [isReenabling, setIsReenabling] = useState(false);
  const [reenableError, setReenableError] = useState<string | null>(null);

  const disable = useCallback(async () => {
    if (account === null) return false;
    setIsDisabling(true);
    try {
      await setStatus({ account, status: 'INACTIVE' }).unwrap();
      dispatch(showAlert({ type: Variants.SUCCESS, message: copy.toasts.disabled(vendorName) }));
      // A completed sync from before disabling has nothing left to report —
      // don't let a stale "just completed" banner outlive the integration
      // being paused.
      dispatch(syncCompletionCleared());
      return true;
    } catch (error: unknown) {
      dispatch(showAlert({ type: Variants.ERROR, message: describeStatusChangeError(error, 'disable') }));
      return false;
    } finally {
      setIsDisabling(false);
    }
  }, [account, dispatch, setStatus, vendorName]);

  const reenable = useCallback(async () => {
    if (account === null) return false;
    setIsReenabling(true);
    setReenableError(null);
    try {
      await setStatus({ account, status: 'ACTIVE' }).unwrap();
      dispatch(showAlert({ type: Variants.SUCCESS, message: copy.toasts.reenabled(vendorName) }));
      // Fire-and-forget: progress (and any fast failure, via the sync log)
      // shows wherever useSyncProgress is rendered. Re-enable itself has
      // already succeeded by this point.
      void startSync(pmsSource);
      return true;
    } catch (error: unknown) {
      setReenableError(describeStatusChangeError(error, 'reenable'));
      return false;
    } finally {
      setIsReenabling(false);
    }
  }, [account, dispatch, pmsSource, setStatus, startSync, vendorName]);

  return { disable, reenable, isDisabling, isReenabling, reenableError };
}
